/**
 * FlakeGlints — a reusable, footprint-filtered STATISTICAL flake model (shader gaps program round 4, material models,
 * 2026-09-30): mica in rock, granular snow, sand, metallic-flake paint.
 *
 * After Thomas Deliot & Laurent Belcour, "Real-Time Rendering of Glinty Appearances using Distributed Binomial Laws on
 * Anisotropic Grids", CGF 42(8) / HPG 2023 (arXiv 2306.05051), itself in the line of Zirr & Kaplanyan 2016 (real-time
 * glints by counting the flakes of a pixel footprint) and Chermain et al. 2020/2021 (procedural glints whose statistics
 * follow the footprint). Written from the papers' math (no code was ported: the public HDRP sample carries no licence).
 *
 * THE MODEL. A surface carries `density` flakes per unit area (in the coordinate the caller hands in — geometry space
 * for a rock, so the flakes ride it and an origin rebase changes nothing). Each flake is a tiny mirror whose normal is
 * drawn from a GGX distribution of roughness α_f around the shading normal. For the half vector h of a light, a flake
 * reflects the light to the eye with probability p = R·D(h)/D(n) (R ∈ (0,1] the glint RATIO: small R ⇒ fewer, brighter
 * glints). A pixel whose footprint holds N flakes sees a count c ~ Binomial(N, p), and the glint NDF is
 *     D_glint = D(n)/R · c/N,       E[D_glint] = D(n)/R · p = D(h)            (Deliot & Belcour, Eq. 7)
 * — the smooth GGX lobe in EXPECTATION at every footprint, and Var[c/N] = p(1−p)/N → 0 as the footprint grows: the
 * sparkle converges to the averaged BRDF at distance (the average brightness is constant as the flakes go unresolved).
 * The lobe is added to the direct specular: k · F(v·h) · Vis_GGX(α_f) · D_glint · (n·l) · E_light, k the flake
 * coverage; the base albedo is scaled by (1 − k·F0) at apply time (the flakes' share of the surface — the energy budget).
 * Direct light only (the sun / lamps — never the environment: an IBL glint is Kneiphof et al. 2025, not built).
 *
 * THE FOOTPRINT (the "distributed binomial law"): the pixel's footprint area A = |∂P/∂x × ∂P/∂y| (taken ONCE at the top
 * of the lighting stack — THE LAZY-VAR LAW) holds N = density·A flakes (≥ 1). Cells of a power-of-two grid in the stable
 * coordinate carry the random numbers: level ℓ has cells of area 4^ℓ/density; the footprint lies between the levels
 * ℓ0 = ⌊½log₂(density·A)⌋ and ℓ0 + 1, and N is SPLIT between them by area weights w0 + w1 = 1 — c = b(w0·N) + b(w1·N)
 * is still Binomial(N, p) (a sum of independent binomials), so the statistics are exact while the camera dollies, and a
 * glint cross-fades between the two cell sizes instead of popping. No time term: a still camera never shimmers.
 * The binomial is the paper's gated Gaussian — P(≥1) = 1 − (1 − p)^N, then 1 + max(0, N((N−1)p, (N−1)p(1−p))) — times
 * a deterministic mean correction that makes the expectation exact (`flakeBinomialCpu`; the tests measure it).
 * NOT built: the paper's anisotropic grids (the area picks the level; a grazing footprint's long axis is not followed —
 * the per-pixel count is still right, the spatial correlation along it is not).
 */
import {
  float, vec3, uint, dot, normalize, max, min, clamp, sqrt, log, log2, exp, cos, floor, dFdx, dFdy, cross, length, pow,
  step, normalView, positionViewDirection, uniform, materialColor, vec2, smoothstep, mix,
} from 'three/tsl';
import type * as THREE from 'three/webgpu';
import { addDirectHook, type DirectHook } from './EONDiffuse.js';

type N = any;

export interface FlakeConfig {
  /** Flakes per unit area of `coord` (default 4000). */
  density?: number;
  /** The flake-normal GGX roughness α_f (default 0.4). */
  flakeRoughness?: number;
  /** Deliot–Belcour's glint ratio R ∈ (0, 1] (default 0.1). */
  ratio?: number;
  /** The flakes' coverage k ∈ [0, 1] (default 0.2). */
  coverage?: number;
  /** The flakes' normal-incidence reflectance (default 0.3 — mica's pearly layers; a metallic paint flake ≈ 0.9). */
  f0?: number;
  /** Tint of the glint (default white). */
  color?: [number, number, number];
}
export interface FlakeNodes {
  /** A STABLE 2D coordinate (geometry / UV / absolute space — never render space). */
  coord: N;
  /** A STABLE 3D position in the same units — its screen derivatives give the footprint area. */
  position: N;
  /** 0..1 live mask (e.g. the kill switch or a per-fragment layer weight). Default 1. */
  mask?: N;
}
export interface FlakeHandle {
  coverage: { value: number };
  intensity: { value: number };
}

// ─── CPU oracles ──────────────────────────────────────────────────────────────────────────────────────────────────

/** D(h)/D(n) for GGX of roughness α (α = perceptual²) at cos θh — 1 at the normal. */
export function ggxRatioCpu(cosH: number, alpha: number): number {
  const a2 = alpha * alpha, c2 = cosH * cosH, d = c2 * (a2 - 1) + 1;
  return (a2 * a2) / (d * d);
}
/** The per-flake hit probability p = R·D(h)/D(n). */
export function flakeHitProbabilityCpu(cosH: number, alpha: number, ratio: number): number {
  return Math.min(1, ratio * ggxRatioCpu(cosH, alpha));
}
/** GGX D(h). */
export function ggxDCpuFlake(cosH: number, alpha: number): number {
  const a2 = alpha * alpha, d = cosH * cosH * (a2 - 1) + 1;
  return a2 / (Math.PI * d * d);
}
/**
 * The gated-Gaussian binomial sample (u0 the gate's uniform, g a standard normal): 0 unless u0 < P(≥1), else
 * 1 + max(0, μ + σg) (μ = (N−1)p, σ² = (N−1)p(1−p) — Deliot & Belcour's conditional form, left continuous: a count is
 * a shading weight, not an integer), times the deterministic correction N·p / (P(≥1)·E[1 + max(0, μ + σg)]) with
 * E[max(0, Y)] = μΦ(μ/σ) + σφ(μ/σ) (Φ by the logistic 1/(1 + e^(−1.702x)), within 1 %) — the mean is exact.
 */
export function flakeBinomialCpu(n: number, p: number, u0: number, g: number): number {
  if (n <= 0 || p <= 0) return 0;
  const p1 = 1 - Math.pow(Math.max(1 - Math.min(p, 1), 1e-6), n);
  if (!(u0 < p1)) return 0;
  const nm1 = Math.max(n - 1, 0);
  const mu = nm1 * p, sigma = Math.max(Math.sqrt(nm1 * p * (1 - p)), 1e-6);
  const z = mu / sigma;
  const eTail = mu / (1 + Math.exp(-1.702 * z)) + sigma * Math.exp(-0.5 * z * z) / Math.sqrt(2 * Math.PI);
  const c = 1 + Math.max(0, mu + sigma * g);
  return c * (n * p) / Math.max(p1 * (1 + eTail), 1e-12);
}
/** The footprint split: N flakes, the two levels and their weights (see the header). */
export function flakeLevelsCpu(area: number, density: number): { n: number; l0: number; w0: number; w1: number } {
  const a = Math.max(area, 1 / density);
  const lam = Math.max(0, 0.5 * Math.log2(a * density));
  const l0 = Math.floor(lam);
  const p0 = Math.pow(4, l0) / density, p1 = Math.pow(4, l0 + 1) / density;
  const w1 = Math.min(1, Math.max(0, (a - p0) / (p1 - p0)));
  return { n: Math.max(1, density * a), l0, w0: 1 - w1, w1 };
}
/** 32-bit PCG hash (the shader's `pcg`), on unsigned ints. */
export function pcgCpu(v: number): number {
  const s = (Math.imul(v >>> 0, 747796405) + 2891336453) >>> 0;
  const w = Math.imul(((s >>> (((s >>> 28) + 4) >>> 0)) ^ s) >>> 0, 277803737) >>> 0;
  return ((w >>> 22) ^ w) >>> 0;
}
const CELL_OFFSET = 1 << 20;
/** The cell's three uniforms (gate, u1, u2) — the shader's `cellRandom`. */
export function cellRandomCpu(cx: number, cy: number, level: number, salt = 0): [number, number, number] {
  const k = pcgCpu((((cx + CELL_OFFSET) >>> 0) ^ pcgCpu((((cy + CELL_OFFSET) >>> 0) ^ pcgCpu((level * 7919 + salt) >>> 0)) >>> 0)) >>> 0);
  const a = pcgCpu(k), b = pcgCpu(a), c = pcgCpu(b);
  return [a / 4294967296, b / 4294967296, c / 4294967296];
}
/**
 * THE FLAKE'S SHAPE when magnified. While a pixel is smaller than a level-0 cell (a flake), the count is carried only
 * inside a disc of radius `FLAKE_DISC_R` (soft edge) at a hashed offset in the cell, divided by the disc's mean cover —
 * a flake is a speck, not the cell's square, and the cell's mean is unchanged. The weight fades with the pixel's size
 * relative to the cell (`magnification` = 1 − √(area·density), 0 once a pixel covers a cell).
 */
export const FLAKE_DISC_R = 0.34;
export const FLAKE_DISC_EDGE = 0.05;
export const FLAKE_DISC_JITTER = 0.12;
const discAt = (d: number): number => { const t = Math.min(1, Math.max(0, (FLAKE_DISC_R + FLAKE_DISC_EDGE - d) / (2 * FLAKE_DISC_EDGE))); return t * t * (3 - 2 * t); };
/** The disc's mean over its cell (numerically: the shader divides by it). */
export const FLAKE_DISC_MEAN = ((): number => {
  let s = 0; const k = 200;
  for (let j = 0; j < k; j++) for (let i = 0; i < k; i++) s += discAt(Math.hypot((i + 0.5) / k - 0.5, (j + 0.5) / k - 0.5));
  return s / (k * k);
})();
export function flakeDiscFactorCpu(fx: number, fy: number, cx: number, cy: number, area: number, density: number): number {
  const m = Math.min(1, Math.max(0, 1 - Math.sqrt(area * density)));
  const disc = discAt(Math.hypot(fx - cx, fy - cy)) / FLAKE_DISC_MEAN;
  return 1 + (disc - 1) * m;
}

/** The glint NDF at one pixel (CPU mirror of the shader): coordinate (x, y), footprint area, cos θh. */
export function flakeDGlintCpu(x: number, y: number, area: number, cosH: number, cfg: Required<Omit<FlakeConfig, 'color' | 'coverage' | 'f0'>>): number {
  const { n, l0, w0, w1 } = flakeLevelsCpu(area, cfg.density);
  const p = flakeHitProbabilityCpu(cosH, cfg.flakeRoughness, cfg.ratio);
  let c = 0;
  for (const [lv, w] of [[l0, w0], [l0 + 1, w1]] as const) {
    if (w <= 0) continue;
    const s = Math.pow(2, lv) / Math.sqrt(cfg.density);
    const [u0, u1, u2] = cellRandomCpu(Math.floor(x / s), Math.floor(y / s), lv);
    const g = Math.sqrt(-2 * Math.log(Math.max(u1, 1e-7))) * Math.cos(2 * Math.PI * u2);
    let b = flakeBinomialCpu(w * n, p, u0, g);
    if (lv === l0) {
      const [v0, v1] = cellRandomCpu(Math.floor(x / s), Math.floor(y / s), lv + 101);
      const fx = x / s - Math.floor(x / s), fy = y / s - Math.floor(y / s);
      b *= flakeDiscFactorCpu(fx, fy, 0.5 + (v0 - 0.5) * 2 * FLAKE_DISC_JITTER, 0.5 + (v1 - 0.5) * 2 * FLAKE_DISC_JITTER, area, cfg.density);
    }
    c += b;
  }
  const dN = 1 / (Math.PI * cfg.flakeRoughness * cfg.flakeRoughness);
  return (dN / cfg.ratio) * c / n;
}

// ─── TSL ──────────────────────────────────────────────────────────────────────────────────────────────────────────

const pcg = (v: N): N => {
  const s: N = v.mul(uint(747796405)).add(uint(2891336453));
  const w: N = s.shiftRight(s.shiftRight(uint(28)).add(uint(4))).bitXor(s).mul(uint(277803737));
  return w.shiftRight(uint(22)).bitXor(w);
};
/** Three uniforms for a cell (the CPU mirror is `cellRandomCpu`). Cells are offset positive (a uint cast of a negative saturates). */
function cellRandom(cx: N, cy: N, level: N): N {
  const lk: N = pcg(uint(level.mul(7919)));
  const k: N = pcg(uint(cx.add(CELL_OFFSET)).bitXor(pcg(uint(cy.add(CELL_OFFSET)).bitXor(lk))));
  const a: N = pcg(k), b: N = pcg(a), c: N = pcg(b);
  const inv = 1 / 4294967296;
  return vec3(a.toFloat().mul(inv), b.toFloat().mul(inv), c.toFloat().mul(inv));
}
/** The gated-Gaussian binomial (see `flakeBinomialCpu`). */
function binomial(n: N, p: N, r: N): N {
  const p1: N = float(1).sub(pow(max(float(1).sub(p), float(1e-6)), n));
  const nm1: N = max(n.sub(1), float(0));
  const mu: N = nm1.mul(p), sigma: N = max(sqrt(nm1.mul(p).mul(float(1).sub(p))), float(1e-6));
  const g: N = sqrt(log(max(r.y, float(1e-7))).mul(-2)).mul(cos(r.z.mul(Math.PI * 2)));
  const z: N = mu.div(sigma);
  const eTail: N = mu.div(exp(z.mul(-1.702)).add(1)).add(sigma.mul(exp(z.mul(z).mul(-0.5))).mul(1 / Math.sqrt(2 * Math.PI)));
  const c: N = max(float(0), mu.add(sigma.mul(g))).add(1);
  const gate: N = step(r.x, p1).mul(step(float(1e-9), p)); // r.x < p1 (and nothing at p = 0)
  return c.mul(gate).mul(n.mul(p).div(max(p1.mul(eTail.add(1)), float(1e-12))));
}

export const flakeStats = { applied: 0 };

/**
 * Add the flake lobe to a MeshStandard(Node)Material (composes with an EON diffuse — one lighting model carries both).
 * `nodes.coord` / `nodes.position` must be STABLE (geometry, UV or absolute space).
 */
export function applyFlakeGlints(material: THREE.Material, nodes: FlakeNodes, cfg: FlakeConfig = {}): FlakeHandle {
  const density = cfg.density ?? 4000, alpha = cfg.flakeRoughness ?? 0.4, ratio = cfg.ratio ?? 0.1;
  const f0 = cfg.f0 ?? 0.3, col = cfg.color ?? [1, 1, 1];
  const uCoverage = uniform(cfg.coverage ?? 0.2), uIntensity = uniform(1);
  const mask: N = nodes.mask ?? float(1);
  const makeHook = (): DirectHook => {
    let nV: N = null, w1V: N = null, r0V: N = null, r1V: N = null, discV: N = null;
    return {
      start(): void {
        // the footprint, ONCE, in uniform flow (THE LAZY-VAR LAW: assigned vars are declared where they are made)
        const area: N = float(0).toVar('flkArea');
        area.assign(length(cross(dFdx(nodes.position), dFdy(nodes.position))));
        const a: N = max(area, float(1 / density));
        const lam: N = max(float(0), log2(a.mul(density)).mul(0.5));
        const l0: N = floor(lam).toVar('flkL0');
        const p0: N = exp(l0.mul(Math.log(4))).div(density);
        const w1: N = clamp(a.sub(p0).div(p0.mul(3)), 0, 1).toVar('flkW1'); // (a − P̲)/(P̄ − P̲), P̄ = 4·P̲
        const s0: N = exp(l0.mul(Math.LN2)).div(Math.sqrt(density));
        const c0: N = floor(nodes.coord.div(s0)), c1: N = floor(nodes.coord.div(s0.mul(2)));
        const r0: N = vec3(0, 0, 0).toVar('flkR0'), r1: N = vec3(0, 0, 0).toVar('flkR1');
        r0.assign(cellRandom(c0.x, c0.y, l0));
        r1.assign(cellRandom(c1.x, c1.y, l0.add(1)));
        const n: N = float(1).toVar('flkN');
        n.assign(max(float(1), a.mul(density)));
        // the flake's shape while magnified (see FLAKE_DISC_R): a speck at a hashed offset, the cell's mean kept
        const disc: N = float(1).toVar('flkDisc');
        const rc: N = cellRandom(c0.x, c0.y, l0.add(101));
        const fr: N = nodes.coord.div(s0).sub(c0);
        const ctr: N = vec2(rc.x, rc.y).sub(0.5).mul(2 * FLAKE_DISC_JITTER).add(0.5);
        const inDisc: N = float(1).sub(smoothstep(float(FLAKE_DISC_R - FLAKE_DISC_EDGE), float(FLAKE_DISC_R + FLAKE_DISC_EDGE), length(fr.sub(ctr))));
        const mag: N = clamp(float(1).sub(sqrt(area.mul(density))), 0, 1);
        disc.assign(mix(float(1), inDisc.div(FLAKE_DISC_MEAN), mag));
        nV = n; w1V = w1; r0V = r0; r1V = r1; discV = disc;
      },
      direct({ lightDirection, lightColor, reflectedLight }): void {
        if (!nV) return;
        const nrm: N = normalView, v: N = positionViewDirection;
        const h: N = normalize(lightDirection.add(v));
        const nh: N = clamp(dot(nrm, h), 0, 1), nl: N = clamp(dot(nrm, lightDirection), 0, 1), nv: N = clamp(dot(nrm, v), 1e-4, 1);
        const vh: N = clamp(dot(v, h), 0, 1);
        const a2 = alpha * alpha;
        const dd: N = nh.mul(nh).mul(a2 - 1).add(1);
        const p: N = min(float(ratio * a2 * a2).div(dd.mul(dd)), float(1));
        const cnt: N = binomial(nV.mul(float(1).sub(w1V)), p, r0V).mul(discV).add(binomial(nV.mul(w1V), p, r1V));
        const dGlint: N = cnt.div(nV).mul(1 / (Math.PI * a2 * ratio));
        const fres: N = float(f0).add(float(1 - f0).mul(pow(float(1).sub(vh), 5)));
        const vis: N = float(0.5).div(max(nl.mul(sqrt(nv.mul(nv).mul(1 - a2).add(a2))).add(nv.mul(sqrt(nl.mul(nl).mul(1 - a2).add(a2)))), float(1e-5)));
        const lobe: N = vec3(col[0], col[1], col[2]).mul(fres.mul(vis).mul(dGlint).mul(nl).mul(uCoverage).mul(uIntensity).mul(mask));
        reflectedLight.directSpecular.addAssign(lightColor.mul(lobe));
      },
    };
  };
  addDirectHook(material, makeHook, uCoverage);
  // the energy budget: the flakes' share of the surface reflects through the lobe, not the diffuse (opaque materials:
  // a scalar on the colour node — alpha is not read)
  const m = material as unknown as { colorNode?: N; needsUpdate: boolean };
  m.colorNode = (m.colorNode ?? materialColor).mul(float(1).sub(uCoverage.mul(f0)));
  m.needsUpdate = true;
  flakeStats.applied++;
  return { coverage: uCoverage as unknown as { value: number }, intensity: uIntensity as unknown as { value: number } };
}
